/**
 * Lusty Chic Fit — deterministic recommendation scoring engine (Phase 6, revised Phase 6b).
 * Namespace: window.LustyChicFit.Engine (LustyChicFit reserved in Phase 1 / CLAUDE.md).
 *
 * No AI, no randomness, no network calls. Same profile + same product data always
 * produces the same result. Pure functions only — this file never touches the DOM or
 * localStorage; assets/lc-fit-results.js owns rendering and reads the saved profile.
 *
 * Expected profile shape (matches assets/lc-fit-quiz.js emptyProfile() exactly):
 *   { body_shape: 'Apple'|'Pear'|'Hourglass'|'Rectangle'|'Inverted Triangle'|null,
 *     body_shape_unsure: bool, age_range, height, rise: 'Low'|'Mid'|'High'|null,
 *     leg_shape: string[] (e.g. ['Skinny','Straight'] — a legacy single-string value is
 *     also accepted and treated as a one-item selection), fit_goals: string[] }
 *
 * Expected product shape (matches the JSON payload built in sections/lc-fit-results.liquid):
 *   { id, handle, title, available: bool, bodyShapes: string[], waistRise: string|null,
 *     legFit: string[], fitGoals: string[], stretchLevel: string|null }
 *
 * Phase 6b revision — the customer-facing score is now confidence-adjusted, not just the
 * ranking order. A product scored against only a fraction of the 95-point model can no
 * longer display 100% just because the categories it happens to have data for all
 * matched. See scoreProduct() for the exact formula and worked examples in the Phase 6b
 * report.
 *
 * UX correction (2026-09-20) — leg_shape is now an unlimited multi-select array on the
 * profile (previously a single string). scoreLegFit was rewritten to use the exact same
 * proportional-credit pattern scoreFitGoals already used for its own array-valued
 * customer preference: earned = (matches / customerSelections) * WEIGHTS.legFit. This
 * mechanically guarantees a broader selection can never increase the score for a fixed
 * match rate — picking more fits only dilutes per-fit credit, never inflates it.
 * WEIGHTS.legFit itself, and every other category's weight and formula, are unchanged.
 */
window.LustyChicFit = window.LustyChicFit || {};

(function (ns) {
  'use strict';

  // 6.4 — base weights. Age's 5 points are never awarded and never redistributed: the
  // brief requires age to stay a weak, non-classifying signal, so it is left out of
  // WEIGHTS entirely rather than scored as a permanent 0. Height has no usable
  // product-level data (6.5–6.11) and is likewise never scored.
  var WEIGHTS = {
    bodyShape: 35,
    fitGoals: 25,
    rise: 15,
    legFit: 10,
    stretch: 10
  };

  // Sum of every category that could ever be scored (Age's 5pts excluded, see above).
  // Used as the denominator for "completeness" — how much of the full model this
  // product+customer pair could actually be evaluated against.
  var MAX_POSSIBLE_POINTS = WEIGHTS.bodyShape + WEIGHTS.fitGoals + WEIGHTS.rise + WEIGHTS.legFit + WEIGHTS.stretch;

  // Phase 6b Task 3 — a product must have at least this many available_points (i.e. at
  // least this much of the model actually be scorable for this customer+product pair)
  // before any numeric score is shown at all. 35 is deliberately not "two categories" —
  // Body Shape alone is worth 35, so a single well-evidenced category can clear the bar
  // on its own, exactly as the brief specifies. Below this, the product is excluded from
  // personalised results entirely (see rankProducts) rather than shown with a placeholder
  // "Fit data being completed" label — a bare card with no score and no real reasons adds
  // clutter to a curated results page without helping the customer, and the product stays
  // fully visible via ordinary collection browsing regardless.
  var MIN_DATA_POINTS = 35;

  // 6.20 / Phase 6b Task 3 — single configurable threshold location, now checked against
  // the confidence-adjusted displayedScore (see scoreProduct), not the raw compatibility.
  var MIN_MATCH_THRESHOLD = 60;

  // The only five real body-shape metaobjects (Phase 2/3). Any other referenced label —
  // e.g. the inactive "Curvy Fit" / "Waist Gap Friendly" / "Fuller Thigh Friendly"
  // entries some products still reference — is defensively ignored rather than matched.
  var CANONICAL_SHAPES = ['Apple', 'Pear', 'Hourglass', 'Rectangle', 'Inverted Triangle'];

  // Customer-facing display strings, kept identical to assets/lc-fit-quiz.js's own
  // RISE_DISPLAY / LEG_DISPLAY maps so reason text matches the quiz's own vocabulary.
  var RISE_DISPLAY = { Low: 'Low Rise', Mid: 'Mid Rise', High: 'High Rise' };
  var LEG_DISPLAY = { Skinny: 'Skinny', Slim: 'Slim', Straight: 'Straight', Flare: 'Flare', Wide: 'Wide Leg' };

  var STRETCH_POINTS = { 'Very High': 10, High: 8, Medium: 4, Low: 0 };

  // 6.23 — plain-language fit-goal reasons. Two of these ("Define Waist", "Maximum
  // Stretch") are given verbatim in the brief; the rest are this agent's reasoned
  // extension in the same voice, since fit_goals has zero product-side data today (see
  // the Phase 6 report) and none of these can fire until that data exists.
  var FIT_GOAL_REASONS = {
    'Define Waist': 'Designed to define your waist',
    'Smooth Tummy': 'Designed to smooth your tummy',
    'Enhance Curves': 'Designed to enhance your curves',
    'Shape Bum': 'Designed to shape and lift',
    'Balance Hips': 'Designed to balance your hips',
    'Longer-Looking Legs': 'Designed to create a longer leg line',
    'All-Day Comfort': 'Built for all-day comfort',
    'Maximum Stretch': 'High-stretch fabric for all-day comfort',
    'Sculpted Fit': 'Designed for a sculpted fit'
  };

  function isNonEmptyString(v) {
    return typeof v === 'string' && v !== '';
  }

  function hasAnswer(v) {
    if (v === null || typeof v === 'undefined') return false;
    if (typeof v === 'string') return v !== '';
    if (v instanceof Array) return v.length > 0;
    return true;
  }

  // Defensive against either a resolved string or an unresolved { label } object.
  // Confirmed live (Phase 6b) that lc-fit-results.liquid's `ref.label` resolution
  // correctly produces plain strings ("Hourglass Shape", "High", "Slim", etc.) in the
  // actual rendered payload — this fallback is now a belt-and-suspenders safeguard
  // rather than a live unknown.
  function normalizeLabel(entry) {
    if (isNonEmptyString(entry)) return entry;
    if (entry && isNonEmptyString(entry.label)) return entry.label;
    return null;
  }

  function normalizeList(list) {
    var out = [];
    if (!list || !list.length) return out;
    for (var i = 0; i < list.length; i++) {
      var v = normalizeLabel(list[i]);
      if (v) out.push(v);
    }
    return out;
  }

  function normalizeShapeLabel(rawLabel) {
    var label = normalizeLabel(rawLabel);
    if (!label) return null;
    var stripped = label.replace(/\s*Shape\s*$/i, '').trim();
    return CANONICAL_SHAPES.indexOf(stripped) !== -1 ? stripped : null;
  }

  // --- Category scorers -----------------------------------------------------------
  // Each returns null when the category cannot be honestly scored for this specific
  // (profile, product) pair — either the customer never answered it, or the product has
  // no data for it. A null category is excluded from both earned and available points
  // (6.12); it never becomes a silent 0. As of Phase 6b, fit_goals/stretch_level have
  // been populated (with real evidence, or left NEEDS MANUAL REVIEW) across the eligible
  // pool — see the Phase 6b report's coverage table for exactly which products still
  // fall back to this null-exclusion for which category.

  function scoreBodyShape(profile, product) {
    if (profile.body_shape_unsure) return null; // 6.5 edge case — excluded, not penalised
    if (!hasAnswer(profile.body_shape)) return null;
    var productShapes = [];
    var rawShapes = product.bodyShapes || [];
    for (var i = 0; i < rawShapes.length; i++) {
      var norm = normalizeShapeLabel(rawShapes[i]);
      if (norm) productShapes.push(norm);
    }
    if (!productShapes.length) return null;
    var matched = productShapes.indexOf(profile.body_shape) !== -1;
    return { earned: matched ? WEIGHTS.bodyShape : 0, available: WEIGHTS.bodyShape, matched: matched };
  }

  function scoreFitGoals(profile, product) {
    var goals = hasAnswer(profile.fit_goals) ? profile.fit_goals : [];
    if (!goals.length) return null; // 6.5 — never divide by zero
    var productGoals = normalizeList(product.fitGoals);
    if (!productGoals.length) return null; // no product data — excluded, not penalised
    var matches = [];
    for (var i = 0; i < goals.length; i++) {
      if (productGoals.indexOf(goals[i]) !== -1) matches.push(goals[i]);
    }
    var earned = (matches.length / goals.length) * WEIGHTS.fitGoals;
    return { earned: earned, available: WEIGHTS.fitGoals, matched: matches.length > 0, matchedGoals: matches };
  }

  function scoreRise(profile, product) {
    if (!hasAnswer(profile.rise)) return null;
    var productRise = normalizeLabel(product.waistRise);
    if (!productRise) return null;
    var matched = productRise === profile.rise;
    return { earned: matched ? WEIGHTS.rise : 0, available: WEIGHTS.rise, matched: matched };
  }

  // UX correction (2026-09-20): leg_shape is now an unlimited multi-select array.
  // Rewritten to use the identical proportional-credit pattern as scoreFitGoals above,
  // so picking more fits can only dilute per-fit credit, never inflate the score. A
  // legacy single-string profile value (pre-correction) is normalised to a one-item
  // array here, transparently, with no migration step required.
  function scoreLegFit(profile, product) {
    var prefsRaw = hasAnswer(profile.leg_shape) ? profile.leg_shape : [];
    var prefs = Array.isArray(prefsRaw) ? prefsRaw : [prefsRaw];
    if (!prefs.length) return null;
    var productFits = normalizeList(product.legFit);
    if (!productFits.length) return null;
    var matches = [];
    for (var i = 0; i < prefs.length; i++) {
      if (productFits.indexOf(prefs[i]) !== -1) matches.push(prefs[i]);
    }
    var earned = (matches.length / prefs.length) * WEIGHTS.legFit;
    return { earned: earned, available: WEIGHTS.legFit, matched: matches.length > 0, matchedFits: matches };
  }

  function scoreStretch(profile, product) {
    var goals = hasAnswer(profile.fit_goals) ? profile.fit_goals : [];
    var prioritised = goals.indexOf('Maximum Stretch') !== -1 || goals.indexOf('All-Day Comfort') !== -1;
    if (!prioritised) return null; // 6.5 — only scored when the customer prioritised it
    var level = product.stretchLevel;
    if (!isNonEmptyString(level) || typeof STRETCH_POINTS[level] === 'undefined') return null;
    var points = STRETCH_POINTS[level];
    return { earned: points, available: WEIGHTS.stretch, matched: points > 0 };
  }

  var CATEGORIES = [
    { key: 'bodyShape', score: scoreBodyShape },
    { key: 'fitGoals', score: scoreFitGoals },
    { key: 'rise', score: scoreRise },
    { key: 'legFit', score: scoreLegFit },
    { key: 'stretch', score: scoreStretch }
  ];

  /**
   * Scores one product against one profile.
   *
   * Phase 6b formula (replaces the Phase 6 "honest % + separate ranking-only confidence"
   * split, after real testing showed a product scored on only Rise+Leg/Fit — 25 of 95
   * possible points — could display a misleading 100% just because both of its scored
   * categories happened to match):
   *
   *   rawCompatibility = round(earned / available * 100)      — % of the SCORABLE points earned
   *   completeness     = available / 95                        — how much of the full model applied
   *   confidenceFactor = 0.5 + 0.5 * completeness               — bounded 0.5 .. 1.0
   *   displayedScore   = round(rawCompatibility * confidenceFactor)
   *
   * The 0.5 floor means a product is never marked down more than half for having thin
   * data, but a perfect score on a sliver of the model can never show as a perfect score
   * to the customer. Worked examples (see the Phase 6b report for more):
   *   - Rise + Leg/Fit only (25/95 available), both match: raw 100%, completeness 26.3%,
   *     confidenceFactor 0.632, displayedScore = round(100 * 0.632) = 63%.
   *   - Body Shape alone (35/95 available), matches: raw 100%, completeness 36.8%,
   *     confidenceFactor 0.684, displayedScore = round(100 * 0.684) = 68%.
   *   - Fully classified (95/95 available), all match: raw 100%, completeness 100%,
   *     confidenceFactor 1.0, displayedScore = 100%.
   *
   * displayedScore is the ONLY number ever shown to the customer ("X% Fit Match") and is
   * also the primary sort key (see rankProducts) — there is no separate hidden ranking
   * value anymore, which is simpler and removes the risk of the shown number and the
   * sort order disagreeing.
   */
  function scoreProduct(profile, product) {
    var earned = 0;
    var available = 0;
    var categories = {};

    for (var i = 0; i < CATEGORIES.length; i++) {
      var def = CATEGORIES[i];
      var result = def.score(profile, product);
      categories[def.key] = result;
      if (result) {
        earned += result.earned;
        available += result.available;
      }
    }

    var rawCompatibility = available > 0 ? Math.round((earned / available) * 100) : 0;
    var completeness = available / MAX_POSSIBLE_POINTS;
    var confidenceFactor = 0.5 + 0.5 * completeness;
    var displayedScore = available > 0 ? Math.round(rawCompatibility * confidenceFactor) : 0;

    // Task 3 gate: a product needs at least MIN_DATA_POINTS of scorable model before it's
    // eligible to show a score at all, AND its confidence-adjusted score must clear
    // MIN_MATCH_THRESHOLD. Both must pass.
    var qualifies = available >= MIN_DATA_POINTS && displayedScore >= MIN_MATCH_THRESHOLD;

    return {
      productId: product.id,
      earnedPoints: earned,
      availablePoints: available,
      maxPossiblePoints: MAX_POSSIBLE_POINTS,
      rawCompatibility: rawCompatibility,
      completeness: completeness,
      confidenceFactor: confidenceFactor,
      displayedScore: displayedScore,
      qualifies: qualifies,
      categories: categories
    };
  }

  /**
   * Generates the 2–3 strongest, genuinely-earned match reasons for a product, ordered
   * by category weight (6.23). Never invents a reason for an unmatched or unscored
   * category.
   */
  function getMatchReasons(profile, product, scoreResult) {
    var result = scoreResult || scoreProduct(profile, product);
    var cats = result.categories;
    var reasons = [];

    if (cats.bodyShape && cats.bodyShape.matched) {
      reasons.push({ weight: WEIGHTS.bodyShape, text: 'Works well with your ' + profile.body_shape + ' profile' });
    }
    if (cats.fitGoals && cats.fitGoals.matched) {
      var goalMatches = cats.fitGoals.matchedGoals || [];
      for (var i = 0; i < goalMatches.length; i++) {
        var text = FIT_GOAL_REASONS[goalMatches[i]];
        if (text) reasons.push({ weight: WEIGHTS.fitGoals, text: text });
      }
    }
    if (cats.rise && cats.rise.matched) {
      reasons.push({ weight: WEIGHTS.rise, text: 'Matches your ' + (RISE_DISPLAY[profile.rise] || profile.rise) + ' preference' });
    }
    if (cats.legFit && cats.legFit.matched) {
      var legMatches = cats.legFit.matchedFits || [];
      var legDisplay = legMatches.map(function (f) { return LEG_DISPLAY[f] || f; }).join(', ');
      if (legDisplay) reasons.push({ weight: WEIGHTS.legFit, text: 'Matches your preferred ' + legDisplay + ' fit' });
    }

    reasons.sort(function (a, b) { return b.weight - a.weight; });

    var texts = [];
    for (var j = 0; j < reasons.length && texts.length < 3; j++) texts.push(reasons[j].text);
    return texts;
  }

  /**
   * Scores and ranks every eligible product against the profile, keeping only products
   * that qualify (Task 3: availablePoints >= MIN_DATA_POINTS AND displayedScore >=
   * MIN_MATCH_THRESHOLD). A product that doesn't qualify is excluded from the returned
   * list entirely — see MIN_DATA_POINTS above for why exclusion was chosen over a "Fit
   * data being completed" placeholder.
   *
   * Sort order (Task 4), most to least significant:
   *   1. availability — an available product always outranks an unavailable one.
   *   2. sufficient fit-data coverage — already guaranteed by the qualifies gate above;
   *      kept as an explicit (normally no-op) comparator so the priority list stays
   *      correct if MIN_DATA_POINTS is ever changed to allow marginal products through.
   *   3. displayedScore (the confidence-adjusted, customer-facing number) — descending.
   *   4. rawCompatibility — descending, tie-break for equal displayedScores.
   *   5. Array.prototype.sort is stable (guaranteed since ES2019), so two products tied
   *      on every key above keep the order they arrived in — i.e. the eligible
   *      collection's own sort order — as the final, deterministic tiebreak.
   *
   * This guarantees a sparsely-classified product can never outrank a well-classified
   * one purely because the few fields it has happened to match perfectly: its
   * displayedScore is already confidence-discounted before sorting ever runs.
   */
  function rankProducts(profile, products) {
    var results = [];
    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      var score = scoreProduct(profile, product);
      if (score.qualifies) {
        results.push({
          product: product,
          score: score,
          reasons: getMatchReasons(profile, product, score)
        });
      }
    }

    results.sort(function (a, b) {
      var availA = a.product.available ? 1 : 0;
      var availB = b.product.available ? 1 : 0;
      if (availB !== availA) return availB - availA;

      var coverageA = a.score.availablePoints >= MIN_DATA_POINTS ? 1 : 0;
      var coverageB = b.score.availablePoints >= MIN_DATA_POINTS ? 1 : 0;
      if (coverageB !== coverageA) return coverageB - coverageA;

      if (b.score.displayedScore !== a.score.displayedScore) return b.score.displayedScore - a.score.displayedScore;
      if (b.score.rawCompatibility !== a.score.rawCompatibility) return b.score.rawCompatibility - a.score.rawCompatibility;
      return 0;
    });

    return results;
  }

  ns.Engine = {
    WEIGHTS: WEIGHTS,
    MAX_POSSIBLE_POINTS: MAX_POSSIBLE_POINTS,
    MIN_DATA_POINTS: MIN_DATA_POINTS,
    MIN_MATCH_THRESHOLD: MIN_MATCH_THRESHOLD,
    scoreProduct: scoreProduct,
    getMatchReasons: getMatchReasons,
    rankProducts: rankProducts,
    normalizeList: normalizeList,
    normalizeLabel: normalizeLabel
  };
})(window.LustyChicFit);
